<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { RotateCcw, Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, PageHeader, Pagination, Section, Select, Spinner, Table, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useCursorPagination } from '../../composables/useCursorPagination.js'
import { formatDateTime, formatInt } from '../llm/llm-format.js'
import { formatCurrency, isoToLocalDateTime, localDateTimeToIso, orderStatusVariant } from './format.js'

const ORDER_STATUSES = ['pending', 'paid', 'canceled', 'failed', 'refunded', 'disputed']
const PAGE_SIZES = [20, 50, 100]

const api = inject('api')
if (!api) throw new Error('BillingOrders: api provider is required')
const { t } = useI18n()
const route = useRoute()

const filters = reactive({ order_id: '', user_id: '', product_id: '', status: '', scope: 'all', since: '', until: '', limit: PAGE_SIZES[0] })
const products = ref([])
const page = useCursorPagination(cursor => api.commerce.listAdminOrders(queryFor(cursor)))

const columns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.billing.orders.table.time'), width: '170px', mono: true },
  { key: 'id', label: t('visualGameAdmin.billing.orders.table.id'), width: '260px', mono: true },
  { key: 'user_id', label: t('visualGameAdmin.billing.orders.table.user'), width: '260px', mono: true },
  { key: 'product_id', label: t('visualGameAdmin.billing.orders.table.product'), width: '180px', mono: true },
  { key: 'status', label: t('visualGameAdmin.billing.orders.table.status'), width: '110px' },
  { key: 'amount_cents', label: t('visualGameAdmin.billing.orders.table.amount'), width: '110px', align: 'right', mono: true },
  { key: 'total_points', label: t('visualGameAdmin.billing.orders.table.points'), width: '120px', align: 'right', mono: true },
  { key: 'points_reversed', label: t('visualGameAdmin.billing.orders.table.reversed'), width: '110px', align: 'right', mono: true },
  { key: 'reversal_shortfall_points', label: t('visualGameAdmin.billing.orders.table.shortfall'), width: '110px', align: 'right', mono: true },
])
const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.billing.orders.filters.allStatuses') },
  ...ORDER_STATUSES.map(status => ({ value: status, label: t(`visualGameAdmin.billing.orders.status.${status}`) })),
])
const productOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.billing.orders.filters.allProducts') },
  ...products.value.map(product => ({ value: product.id, label: `${product.title} · ${product.id}` })),
])
const scopeOptions = computed(() => [
  { value: 'all', label: t('visualGameAdmin.billing.orders.filters.scopeAll') },
  { value: 'shortfall', label: t('visualGameAdmin.billing.orders.filters.scopeShortfall') },
])
const summary = computed(() => t('visualGameAdmin.billing.pager.summary', { page: page.pageNumber.value, n: page.items.value.length }))

onMounted(async () => {
  // 流水页和看板会带着筛选条件深链过来，落地就按它筛。
  filters.order_id = queryText(route.query.order_id)
  filters.user_id = queryText(route.query.user_id)
  filters.product_id = queryText(route.query.product_id)
  filters.status = queryText(route.query.status)
  filters.scope = route.query.shortfall_only === 'true' ? 'shortfall' : 'all'
  filters.since = queryDateTime(route.query.since)
  filters.until = queryDateTime(route.query.until)
  await Promise.all([page.reset(), loadProducts()])
})

async function loadProducts() {
  products.value = (await api.commerce.listAdminProducts()).items
}

function queryText(value) {
  return typeof value === 'string' ? value : ''
}

function queryDateTime(value) {
  return typeof value === 'string' ? isoToLocalDateTime(value) : ''
}

function queryFor(cursor) {
  const query = { limit: filters.limit }
  for (const field of ['order_id', 'user_id', 'product_id']) {
    if (filters[field].trim() !== '') query[field] = filters[field].trim()
  }
  if (filters.status !== '') query.status = filters.status
  if (filters.scope === 'shortfall') query.shortfall_only = true
  const since = localDateTimeToIso(filters.since)
  const until = localDateTimeToIso(filters.until)
  if (since !== null) query.since = since
  if (until !== null) query.until = until
  if (cursor !== null) query.cursor = cursor
  return query
}

function resetFilters() {
  for (const field of ['order_id', 'user_id', 'product_id', 'status', 'since', 'until']) filters[field] = ''
  filters.scope = 'all'
  page.reset()
}

function changePageSize(size) {
  filters.limit = size
  page.reset()
}
</script>

<template>
  <div class="vga-page" data-insp-path="apps/visual-game-admin/src/pages/billing/OrdersPage.vue">
    <PageHeader :title="t('visualGameAdmin.billing.orders.title')" />

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.billing.orders.filters.orderId') }}</label>
          <Input v-model="filters.order_id" size="sm" @keyup.enter="page.reset" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.billing.orders.filters.userId') }}</label>
          <Input v-model="filters.user_id" size="sm" @keyup.enter="page.reset" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.orders.filters.product') }}</label>
          <Select v-model="filters.product_id" :options="productOptions" size="sm" block />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.orders.filters.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" block />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.orders.filters.scope') }}</label>
          <Select v-model="filters.scope" :options="scopeOptions" size="sm" block />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.orders.filters.since') }}</label>
          <Input v-model="filters.since" type="datetime-local" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.orders.filters.until') }}</label>
          <Input v-model="filters.until" type="datetime-local" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="page.loading.value" @click="page.reset">
            <Search :size="14" />{{ t('visualGameAdmin.waitlist.filters.search') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="page.loading.value" @click="resetFilters">
            <RotateCcw :size="14" />{{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <Section :padded="false">
      <div v-if="page.loading.value && page.items.value.length === 0" class="vga-state"><Spinner /></div>
      <div v-else-if="page.error.value" class="vga-state">
        <p class="vga-error">{{ page.error.value }}</p>
        <Button variant="secondary" @click="page.reload">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState v-else-if="page.items.value.length === 0" :title="t('visualGameAdmin.billing.orders.empty')">
        <template #action>
          <Button variant="secondary" @click="resetFilters">{{ t('visualGameAdmin.common.reset') }}</Button>
        </template>
      </EmptyState>
      <Table v-else :columns="columns" :rows="page.items.value">
        <template #created_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #user_id="{ row }">
          <RouterLink class="vga-link" :to="`/users/${row.user_id}`">{{ row.user_id }}</RouterLink>
        </template>
        <template #status="{ row }">
          <Badge :variant="orderStatusVariant(row.status)">{{ t(`visualGameAdmin.billing.orders.status.${row.status}`) }}</Badge>
        </template>
        <template #amount_cents="{ row }">{{ formatCurrency(row.amount_cents, row.currency) }}</template>
        <template #total_points="{ row }">{{ formatInt(row.total_points) }}</template>
        <template #points_reversed="{ row }">{{ formatInt(row.points_reversed) }}</template>
        <template #reversal_shortfall_points="{ row }">
          <span :class="{ 'vga-error': row.reversal_shortfall_points > 0 }">{{ formatInt(row.reversal_shortfall_points) }}</span>
        </template>
      </Table>
    </Section>

    <Pagination
      :has-prev="page.hasPrev.value"
      :has-next="page.hasNext.value"
      :loading="page.loading.value"
      :prev-label="t('visualGameAdmin.billing.pager.prev')"
      :next-label="t('visualGameAdmin.billing.pager.next')"
      :summary="summary"
      :page-size-label="t('visualGameAdmin.billing.pager.pageSize')"
      :page-size="filters.limit"
      :page-size-options="PAGE_SIZES"
      @prev="page.previous"
      @next="page.next"
      @update:page-size="changePageSize"
    />
  </div>
</template>
